// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import styled from 'styled-components';

import { BaseProps, baseSpacing, baseStyles } from '../Base';
import { MessageVariant } from './ChatBubbleContainer';
import { ellipsis } from '../../../utils/style';

interface StyledChatBubbleContainerProps extends BaseProps {
  actions?: any;
  editing?: boolean;
}

interface StyledChatBubbleProps extends BaseProps {
  variant: MessageVariant;
  editable?: boolean;
  highlight?: boolean;
}

export const StyledChatBubbleContainer = styled.div<StyledChatBubbleContainerProps>`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  margin-bottom: ${(props) => (props.editing ? '1rem' : 'unset')};
  position: relative;

  & {
    .chat-time-wrap,
    .chat-loading-wrap {
      align-self: flex-end;
      flex-shrink: 0;
      margin-right: 0.5rem;
      font-size: ${(props) => props.theme.fontSizes.exSmall.fontSize};
      line-height: ${(props) => props.theme.fontSizes.exSmall.lineHeight};
      color: ${(props) => props.theme.colors.greys.grey20};
    }

    .chat-loading-wrap {
      margin-right: 0.25rem;
    }

    .chat-lastread-wrap {
      margin-bottom: 0.25rem;
      display: block;
      text-align: right;
    }

    .chat-icon-wrap {
      flex-shrink: 0;
      margin-right: 0.75rem;
      display: flex;

      img {
        border-radius: ${(props) => props.theme.radii.circle};
        background-color: ${(props) => props.theme.colors.greys.grey20};
        width: 2.5rem;
        height: 2.5rem;
      }
    }

    .chat-bubble-action-popover {
      position: absolute;
      bottom: -9px;
      right: 0.625rem;
      z-index: 1;
      display: flex;
      border-radius: ${(props) => props.theme.radii.circle};
      border: 1px solid ${(props) => props.theme.colors.primary.main};

      &:active {
        opacity: 0.9;
      }
    }
  }

  &.incoming {
    flex-direction: row-reverse;

    .chat-time-wrap {
      margin: 0 0 0 0.5rem;
    }
  }

  ${({ theme }) => theme.mediaQueries.min.md} {
    & {
      .chat-time-wrap,
      .chat-loading-wrap {
        margin-right: 0.75rem;
        font-size: ${(props) => props.theme.fontSizes.small.fontSize};
        font-weight: ${(props) => props.theme.fontSizes.small.fontWeight};
      }

      .chat-loading-wrap {
        margin-right: 0.25rem;
      }

      .chat-icon-wrap {
        margin-right: 1rem;

        img {
          width: 3.25rem;
          height: 3.25rem;
        }
      }

      .chat-bubble-action-popover:hover {
        opacity: 0.9;
      }
    }

    &.incoming {
      .chat-time-wrap {
        margin: 0 0 0 0.75rem;
      }
    }
  }
`;

export const StyledChatBubble = styled.div<StyledChatBubbleProps>`
  padding: 0.75rem;
  background-color: ${(props) =>
    props.editable
      ? props.theme.chatBubble.editable.bgd
      : props.highlight
      ? props.theme.chatBubble.highlight.bgd
      : props.theme.chatBubble[props.variant].bgd};
  border: ${(props) =>
    props.editable
      ? props.theme.chatBubble.editable.border
      : props.highlight
      ? props.theme.chatBubble.highlight.border
      : `1px solid ${props.theme.chatBubble[props.variant].bgd}`};
  border-radius: ${(props) =>
    props.theme.chatBubble[props.variant].borderRadius};
  box-shadow: ${(props) => props.theme.chatBubble.boxShadow};
  max-width: 20.125rem;
  min-width: 0;
  width: ${(props) => (props.editable ? '100%' : 'fit-content')};
  font-size: ${(props) => props.theme.fontSizes.small.fontSize};
  line-height: ${(props) => props.theme.fontSizes.small.lineHeight};
  color: ${(props) =>
    props.editable
      ? props.theme.chatBubble.editable.fontColor
      : props.highlight
      ? props.theme.chatBubble.highlight.fontColor
      : props.theme.chatBubble[props.variant].fontColor};
  position: relative;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;

  & .ch-auto-response {
    position: absolute;
    top: 0;
    left: 1.5rem;
    transform: translateY(-50%);
    z-index: 1;
    font-size: ${(props) => props.theme.fontSizes.text.fontSize};
    line-height: 1;
    font-weight: bold;
    color: ${(props) => props.theme.colors.primary.main};
    background-color: ${(props) => props.theme.colors.info.primary};
    border-radius: ${(props) => props.theme.radii.round};
    border: 1px solid ${(props) => props.theme.colors.primary.main};
    padding: 0.375rem 1rem;
  }

  & .ch-footer {
    margin-top: 0.5rem;
    display: flex;
    align-items: baseline;
  }

  & .ch-sender-name {
    font-size: ${(props) => props.theme.fontSizes.exSmall.fontSize};
    line-height: ${(props) => props.theme.fontSizes.exSmall.lineHeight};
    font-weight: bold;
    ${ellipsis}
  }

  & a {
    color: inherit;
  }

  .ch-input {
    width: 100%;
  }

  .ch-edit-buttons {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    display: flex;
  }

  .ch-edit-button {
    color: ${(props) => props.theme.colors.primary.main};
    font-size: ${(props) => props.theme.fontSizes.small.fontSize};
    line-height: ${(props) => props.theme.fontSizes.small.lineHeight};
    margin-left: 1rem;
  }

  ${baseSpacing};
  ${baseStyles};

  ${({ theme }) => theme.mediaQueries.min.md} {
    padding: 1rem;
    font-size: ${(props) => props.theme.fontSizes.text.fontSize};
    line-height: ${(props) => props.theme.fontSizes.text.lineHeight};

    & .ch-sender-name {
      font-size: ${(props) => props.theme.fontSizes.small.fontSize};
      line-height: ${(props) => props.theme.fontSizes.small.lineHeight};
    }
  }
`;

export const StyledChatWithoutBubble = styled(StyledChatBubbleContainer)`
  & {
    .chat-content-wrap {
      .chat-content {
        display: flex;
        justify-content: flex-end;
      }

      .chat-other-content {
        margin-top: 0.75rem;
        display: flex;
        align-items: center;
        justify-content: flex-end;

        .chat-time-wrap:only-child {
          margin: 0;
        }
      }
    }

    .chat-sender-name {
      font-size: ${(props) => props.theme.fontSizes.exSmall.fontSize};
      line-height: ${(props) => props.theme.fontSizes.exSmall.lineHeight};
      font-weight: bold;
      color: ${(props) => props.theme.colors.primary.main};
      margin-right: 0.5rem;
    }

    .chat-time-wrap {
      align-self: unset;
    }

    .chat-bubble-action-popover {
      position: static;
    }
  }

  &.incoming {
    .chat-content-wrap {
      .chat-content {
        justify-content: flex-start;
      }

      .chat-other-content {
        flex-direction: row-reverse;

        .chat-sender-name {
          margin: 0;
          color: ${(props) => props.theme.colors.greys.grey80};
        }
      }
    }

    .chat-sender-name {
      margin: 0 0 0 0.5rem;
    }
  }

  ${({ theme }) => theme.mediaQueries.min.md} {
    & {
      .chat-sender-name {
        font-size: ${(props) => props.theme.fontSizes.small.fontSize};
        line-height: ${(props) => props.theme.fontSizes.small.lineHeight};
      }
    }
  }
`;
